import type React from "react"
import { useEffect, useMemo } from "react"
import { Link, NavLink, useNavigate } from "react-router-dom"
import styled from "styled-components"
import backgroundImage from "~/assets/arbeidsark-bg-illustration.svg"
import { Button, Form, FormField, Input, Label } from "~/components"
import { FullPageLoader } from "~/components/Loader"
import { captureEvent, identifyUser } from "~/modules/analytics/capture"
import { Margin } from "~/ur-react-components"
import { useForm, useTranslate } from "~/ur-react-hooks"
import { useKeyDown } from "~/utils"
import { trpc } from "~/utils/trpc"
import { setAuthToken } from "./util"

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;

  background-image: url(${backgroundImage});

  ${(props) =>
    props.theme.media.mobile &&
    `
      width: 100%;
  `}
`

const Title = styled.h3`
  font-family: 'Work Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 145%;

  margin-top: 1rem;
  margin-bottom: 1rem;
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 394px;
  box-sizing: border-box;

  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 8px 32px 0 rgba(27, 12, 55, 0.12);
`

/*
const FeideButton = styled.a`
  display: flex;
  appearance: none;
  border: none;
  background-color: #1f4698;
  color: white;
  height: 40px;
  justify-content: center;
  align-items: center;

  margin-bottom: 2rem;
  cursor: pointer;
`
*/

const BottomSection = styled.div`
  text-align: center;
  font-size: 14px;
  margin-top: 2rem;
  margin-bottom: 1rem;
`

const RegisterUserText = styled.p`
  color: ${(props) => props.theme.colors.gray0};
  margin: 0.5rem;
`

const RegisterUserLink = styled(Link)`
  display: block;
  color: ${(props) => props.theme.colors.primary};
  font-weight: bold;
`

const ForgottenPasswordLink = styled(Link)`
  margin-top: 2rem;
  display: block;
  color: ${(props) => props.theme.colors.gray50};
  text-decoration-line: underline;
`

type LoginProps = {}

export const LoginWithPassword: React.FC<LoginProps> = () => {
  const translations = useTranslate({
    login: "common.log-in",
    email: "common.email",
    password: "common.password",
    notAlreadyRegistered: "login.not-already-registered",
    createUser: "login.create-user",
    forgottenPassword: "login.forgotten-password",
    error: {
      createUser: "login.create-user-error",
      emailTaken: "login.email-address-already-in-use",
      wrongUsernameOrPassword: "error.wrong-username-or-password",
      badEmail: "errors.invalid-email-address",
    },
  })

  const nav = useNavigate()

  const {
    formValues,
    formErrors,
    validateForm,
    formChangeHandler: handler,
    submitHandler: submit,
  } = useForm({
    values: {
      email: "",
      password: "",
    },
  })

  const handleEnter = useKeyDown("Enter", submit(handleSubmit))

  const { data: isLoggedIn, isLoading: isLoggedInLoading } =
    trpc.authentication.isLoggedIn.useQuery()

  console.log("isLoggedIn", isLoggedIn)

  const {
    mutate: login,
    error: loginError,
    isPending: loginLoading,
  } = trpc.authentication.login.useMutation({
    onSuccess(data) {
      setAuthToken(data.token)
      if (data.user?.id) {
        identifyUser(data.user.id.toString(), {
          email: data.user.email,
          name: (data.user.firstName + " " + data.user.lastName).trim(),
          is_system_admin: data.user.role === "ADMIN",
        })
      }
      captureEvent("login_success")
      // Force a page reload to reinitialize TRPC client with new token
      window.location.href = "/"
    },
  })

  const submitDisabled = useMemo(
    () => loginLoading || !formValues.email || !formValues.password,
    [loginLoading, formValues.email, formValues.password],
  )

  async function handleSubmit() {
    if (submitDisabled) return

    const result = validateForm()
    if (!result.valid) {
      return
    }

    login({ email: formValues.email, password: formValues.password })
  }

  useEffect(() => {
    if (isLoggedIn) nav("/")
  }, [isLoggedIn, nav])

  if (isLoggedInLoading) return <FullPageLoader />

  return (
    <Wrapper>
      <Card>
        <Title>{translations.login}</Title>

        {/*
        <FeideButton
          href={`https://auth.dataporten.no/oauth/authorization?client_id=${
            import.meta.env.VITE_APP_FEIDE_CLIENT_ID
          }&redirect_uri=${encodeURIComponent(
            import.meta.env.VITE_APP_FEIDE_REDIRECT_URI
          )}&response_type=code&scope=openid`}
        >
          Logg inn med Feide
        </FeideButton>
        */}

        <Form preventDefault onSubmit={submit(handleSubmit)}>
          <FormField margin="1rem">
            <Label>{translations.email}</Label>
            <Input
              value={formValues.email}
              autoFocus
              error={
                formErrors.email ||
                (loginError && translations.error.wrongUsernameOrPassword)
              }
              width="100%"
              disabled={isLoggedInLoading}
              onKeyDown={handleEnter}
              onChange={handler("email")}
            />
          </FormField>
          <FormField>
            <Label>{translations.password}</Label>
            <Input
              value={formValues.password}
              width="100%"
              type="password"
              disabled={isLoggedInLoading}
              onKeyDown={handleEnter}
              onChange={handler("password")}
            />
          </FormField>
          <Margin top="1.5rem">
            <Button
              disabled={submitDisabled}
              onClick={submit(handleSubmit)}
              width="100%"
            >
              {translations.login}
            </Button>
          </Margin>
        </Form>
        <BottomSection>
          <RegisterUserText>
            {translations.notAlreadyRegistered}
          </RegisterUserText>
          <RegisterUserLink to="/register">
            {translations.createUser}
          </RegisterUserLink>
          <ForgottenPasswordLink to="/reset-password">
            {translations.forgottenPassword}
          </ForgottenPasswordLink>
          <NavLink to={"/login"}>
            <div className="pointer mt-8 text-gray-500 text-xs">
              Få lenke på e-post
            </div>
          </NavLink>
        </BottomSection>
      </Card>
    </Wrapper>
  )
}
